import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useParentChildren } from "@/hooks/useParentChildren";
import ChildSelector from "@/components/ChildSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSpreadsheet, Plus, Sparkles, Award, Download, Users2, Send } from "lucide-react";

export default function ExamsPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const { selected: selectedChild } = useParentChildren();

  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ class_id: "", subject: "", date: "", total_marks: 100, term: 1 });
  const [report, setReport] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const isTeacher = ["admin", "principal", "teacher"].includes(user?.role);
  const isStudent = user?.role === "student";

  const load = async () => {
    const params = isParent && selectedChild?.class_id ? { class_id: selectedChild.class_id } : {};
    const { data } = await api.get("/exams", { params });
    setExams(data);
    if (isStudent) {
      const { data: r } = await api.get(`/exams/report/${user.id}`);
      setReport(r);
    } else if (isParent && selectedChild) {
      const { data: r } = await api.get(`/exams/report/${selectedChild.id}`);
      setReport(r);
    }
  };
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild?.id]);

  const create = async () => {
    try {
      await api.post("/exams", { ...form, total_marks: parseInt(form.total_marks, 10), term: parseInt(form.term, 10) });
      toast.success("Exam scheduled");
      setOpen(false);
      setForm({ class_id: "", subject: "", date: "", total_marks: 100, term: 1 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const aiReport = async (r) => {
    setAiOpen(true); setAiText(""); setAiLoading(true);
    const contextStudent = isParent ? selectedChild : user;
    try {
      const { data } = await api.post("/ai/generate", {
        kind: "report_comment", subject: r.subject, grade: contextStudent?.grade || "8",
        student_name: contextStudent?.name, marks: (r.marks / r.total_marks * 100).toFixed(0),
        context: `Term ${r.term} results`,
      });
      setAiText(data.text);
    } finally { setAiLoading(false); }
  };

  // Bulk class report state (teachers/admins/principals)
  const [bulkClassId, setBulkClassId] = useState("");
  const [bulkTerm, setBulkTerm] = useState("all");
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  useEffect(() => {
    if (isTeacher && classes.length > 0 && !bulkClassId) setBulkClassId(classes[0].id);
  }, [isTeacher, classes, bulkClassId]);

  const downloadBulk = () => {
    if (!bulkClassId) return;
    const termQ = bulkTerm !== "all" ? `?term=${bulkTerm}` : "";
    window.open(`${API_BASE}/exams/report/class/${bulkClassId}/pdf${termQ}`, "_blank");
  };

  const shareBulk = async () => {
    if (!bulkClassId) return;
    setSharing(true);
    setShareResult(null);
    try {
      const params = bulkTerm !== "all" ? { term: bulkTerm } : {};
      const { data } = await api.post(`/exams/report/class/${bulkClassId}/share`, null, { params });
      setShareResult(data);
      toast.success(`Shared to ${data.delivered_count}/${data.recipients.length} parent(s)`, {
        description: "MOCKED — real dispatch requires Resend API key",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to share");
    } finally { setSharing(false); }
  };

  return (
    <div className="space-y-6" data-testid="exams-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Assessments</div>
          <h1 className="text-4xl font-bold tracking-tight">Exams & Reports</h1>
          <p className="text-muted-foreground text-sm mt-1">Term exam schedule, marks and AI report comments.</p>
        </div>
        {isTeacher && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-2xl gap-2" data-testid="exam-create-open"><Plus className="h-4 w-4" /> Schedule exam</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>New exam</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger className="rounded-2xl"><SelectValue placeholder="Class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-2xl" />
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl" />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" placeholder="Total marks" value={form.total_marks} onChange={(e) => setForm({ ...form, total_marks: e.target.value })} className="rounded-2xl" />
                  <Select value={String(form.term)} onValueChange={(v) => setForm({ ...form, term: v })}>
                    <SelectTrigger className="rounded-2xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4].map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="rounded-2xl" data-testid="exam-create-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <ChildSelector />

      {isTeacher && (
        <Card className="rounded-2xl border-primary/25 bg-primary/5" data-testid="bulk-report-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users2 className="h-4 w-4 text-primary" /> Class report cards (bulk)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1.5">Class</div>
                <Select value={bulkClassId} onValueChange={setBulkClassId}>
                  <SelectTrigger className="rounded-xl w-48 h-11" data-testid="bulk-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1.5">Term</div>
                <Select value={bulkTerm} onValueChange={setBulkTerm}>
                  <SelectTrigger className="rounded-xl w-32 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All terms</SelectItem>
                    {[1, 2, 3, 4].map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={downloadBulk} disabled={!bulkClassId} className="rounded-xl gap-1 h-11" data-testid="bulk-download">
                <Download className="h-4 w-4" /> Download bulk PDF
              </Button>
              <Button onClick={shareBulk} disabled={!bulkClassId || sharing} variant="outline" className="rounded-xl gap-1 h-11" data-testid="bulk-share">
                <Send className="h-4 w-4" /> {sharing ? "Sharing…" : "Share with parents"}
              </Button>
            </div>
            {shareResult && (
              <div className="mt-4 p-3 rounded-xl bg-background/50 border border-border" data-testid="bulk-share-result">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  MOCKED — {shareResult.delivered_count}/{shareResult.recipients.length} parent(s) would receive an email
                </div>
                <div className="mt-2 divide-y divide-border/60 text-sm">
                  {shareResult.recipients.map((r, i) => (
                    <div key={i} className="py-1.5 flex items-center gap-2">
                      <span className="flex-1 truncate">{r.student_name}</span>
                      <span className="text-xs text-muted-foreground truncate">{r.parent_email}</span>
                      {r.delivered ? (
                        <Badge className="rounded-full bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/20 text-[0.65rem]">delivered</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-full text-[0.65rem]">unlinked</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-primary" /> Upcoming exams</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border/60">
          {exams.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No exams scheduled.</div>}
          {exams.map((e) => (
            <div key={e.id} className="py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><FileSpreadsheet className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="font-medium">{e.subject}</div>
                <div className="text-xs text-muted-foreground">Term {e.term} · {e.total_marks} marks</div>
              </div>
              <Badge variant="outline" className="rounded-full">{e.date}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {(isStudent || isParent) && (
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4 text-primary" />
              {isParent ? `${selectedChild?.name || "Child"}'s report card` : "Your report card"}
            </CardTitle>
            <Button
              size="sm"
              onClick={() => {
                const sid = isParent ? selectedChild?.id : user.id;
                if (!sid) return;
                window.open(`${API_BASE}/exams/report/${sid}/pdf`, "_blank");
              }}
              className="rounded-xl gap-1"
              data-testid="report-download-pdf"
              disabled={isParent && !selectedChild}
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </Button>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            {report.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No marks published yet.</div>}
            {report.map((r) => (
              <div key={r.id} className="py-3 flex items-center gap-3" data-testid={`report-row-${r.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{r.subject}</div>
                  <div className="text-xs text-muted-foreground">Term {r.term} · {r.date}</div>
                </div>
                <div className="font-semibold">{r.marks}/{r.total_marks}</div>
                <Badge className="rounded-full">
                  {r.total_marks ? Math.round(r.marks / r.total_marks * 100) : 0}%
                </Badge>
                <Button size="sm" variant="outline" onClick={() => aiReport(r)} className="rounded-xl gap-1">
                  <Sparkles className="h-3.5 w-3.5" /> AI comment
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI Report Comment</DialogTitle></DialogHeader>
          <div className="min-h-32 p-4 rounded-xl bg-muted/40 whitespace-pre-wrap text-sm leading-relaxed">
            {aiLoading ? "Drafting…" : (aiText || "No comment yet.")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
